import { Children, ReactElement, ReactNode, useMemo, useRef } from 'react'
import { pushToSectionsHistory } from '../service'
import { SectionContext } from './contexts'
import { AnimatedSwitch } from './animations'
import { useRunOnce } from '../utils/hooks'
import { useSectionHistoryTop } from '../store.hooks'
import { RouteTransitions } from '../types'
import { Store } from '../store'

interface PropTypes {
  name: string
  isInitialSection?: boolean
  initialPanel?: string
  transition?: RouteTransitions
  children: ReactNode
}
export function Section({
  name,
  initialPanel,
  isInitialSection,
  children,
  transition,
}: PropTypes) {
  const panelToIndexMapRef = useRef<Record<string, number>>({})

  useRunOnce(() => {
    if (initialPanel) {
      const sectionHistory = Store.getSectionHistory(name)
      if (!sectionHistory.length) {
        sectionHistory.push(initialPanel)
        Store.commit()
      }
    }

    const sectionsHistory = Store.getActiveSectionsHistory()
    if (isInitialSection && !sectionsHistory.length) pushToSectionsHistory(name)

    const map: Record<string, number> = {}
    Children.forEach(children, (child, index) => {
      const panelName = (child as ReactElement).props?.['data-name']
      if (panelName) map[panelName] = index
    })

    panelToIndexMapRef.current = map
  })

  const topPanel = useSectionHistoryTop(name)
  const topPanelIndex = useMemo(
    () => panelToIndexMapRef.current[topPanel] || 0,
    [topPanel],
  )

  return (
    <SectionContext.Provider value={{ name }}>
      {transition ? (
        <AnimatedSwitch activeKey={topPanelIndex} {...transition}>
          {children}
        </AnimatedSwitch>
      ) : (
        children
      )}
    </SectionContext.Provider>
  )
}
